import React, { FC } from 'react';

import { ITheme } from '@/src/assets/interfaces/themeInterface';
import { advInfoFull } from '@/src/assets/objects/advInfo';
import { baseUrl } from '@/src/assets/utils/baseUrl';
import { getImageUrl } from '@/src/assets/utils/getImageUrl';

import { Title } from '../../UI/title/Title';
import Wrapper from '../../UI/wrapper/Wrapper';
import AdvantageList from '../../advantageList/AdvantageList';
import Breadcrumbs from '../../breadcrumbs/Breadcrumbs';
import DemoCard from '../../demoCard/DemoCard';
import Layout from '../../layout/Layout';
import AdsSection from '../../sections/adsSection/AdsSection';
import BundleSection from '../../sections/bundleSection/BundleSection';
import CentralStageSection from '../../sections/centralStageSection/CentralStageSection';
import DesignSection from '../../sections/designSection/DesignSection';
import MainInfo from '../../sections/mainInfo/MainInfo';
import SupportSection from '../../sections/supportSection/SupportSection';

import styles from './Theme.module.scss';

type TTheme = {
    theme: ITheme;
};

const Theme: FC<TTheme> = ({ theme }) => {
    const {
        title,
        price,
        documentationUrl,
        purchaseUrl,
        metaTitle,
        metaDescription,
        showDemoSection,
        demoSectionTitle,
        demoSectionDescription,
        demoList,
        ogImage,
        slug,
        supportSectionTitle,
        supportSectionDescription,
        supportSectionImage,
        ctaGallery,
        ctaSectionDescription,
        ctaSectionTitle,
        themeDesktop,
        themeMobile,
        footerCtaSectionDescription,
        footerCtaSectionTitle,
        footerCtaSectionImage,
    } = theme;

    const choosePreview = demoList && demoList[0];

    const jsonLd = {
        '@context': 'https://schema.org/',
        '@type': 'Product',
        name: metaTitle || 'Theme',
        image: [`${getImageUrl(themeDesktop)}`, `${getImageUrl(themeMobile)}`],
        url: `${baseUrl(`themes/${slug}`)}`,
        description:
            metaDescription ||
            'Harness the ethereal power of this platform to captivate your audience and unleash the full potential of your content.',
        sku: `${slug}`,
        brand: {
            '@type': 'Brand',
            name: 'High Five Themes',
        },
        offers: {
            '@type': 'Offer',
            price: `${price}`,
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
            seller: {
                '@type': 'Organization',
                name: 'Ashstones',
            },
        },
    };

    const heading = 'Essential Theme Features for Enhanced Online Performance';
    const excerpt =
        'More features, more advantages, and more opportunities to grow your online presence.';

    return (
        <Layout
            meta={{
                metaTitle: metaTitle,
                metaDescription: metaDescription,
                metaImageUrl: !!ogImage && getImageUrl(ogImage),
                ogTitle: metaTitle,
                ogDescription: metaDescription,
            }}
            jsonLD={jsonLd}>
            <Wrapper>
                <Breadcrumbs />
            </Wrapper>
            <MainInfo themeData={theme} />
            <section className={styles.demos}>
                <Wrapper>
                    <Title
                        text={demoSectionTitle}
                        size="lg"
                        className={styles['section-heading']}
                    />

                    <p className={`excerpt body-lg excerpt-wrap ${styles.excerpt} `}>
                        {demoSectionDescription}
                    </p>
                    <ul className={styles.themes__list}>
                        {demoList &&
                            demoList.map((demo, i) => (
                                <li
                                    key={`${demo._key}-${demo.demoName}`}
                                    className={styles.tag__item}>
                                    <DemoCard demoData={demo} theme={theme} isThemePage={true} />
                                </li>
                            ))}
                    </ul>
                </Wrapper>
            </section>
            <AdsSection
                price={price}
                preview={choosePreview.demoPreview ?? '/'}
                themeName={title}
                title={ctaSectionTitle}
                gallery={ctaGallery}
                descr={ctaSectionDescription}
                purchase={purchaseUrl}
            />
            <DesignSection demos={demoList.length} features={theme.featuresSection} />
            <BundleSection className={styles.bundle} showLink={true} />
            <section className={styles.advantage__section}>
                <Wrapper>
                    <Title text={heading} size="lg" className={styles['section-heading']} />

                    <p className={`excerpt body-lg excerpt-wrap ${styles.excerpt} `}>{excerpt}</p>
                    <AdvantageList advList={advInfoFull} />
                </Wrapper>
            </section>
            <SupportSection
                title={supportSectionTitle}
                descr={supportSectionDescription}
                bgImage={supportSectionImage}
                docs={documentationUrl}
            />
            <CentralStageSection
                price={price}
                preview={choosePreview.demoPreview ?? '/'}
                themeName={title}
                title={footerCtaSectionTitle}
                descr={footerCtaSectionDescription}
                bgImg={footerCtaSectionImage}
                purchase={purchaseUrl}
            />
        </Layout>
    );
};

export default Theme;
